import type { SupabaseClient } from "@supabase/supabase-js";
import { lazy, type ReactNode, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AdminRoute } from "./components/AdminRoute";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { FilAriane } from "./components/FilAriane";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { StatusBarScrim } from "./components/StatusBarScrim";
import { AuthProvider, useAuth } from "./lib/auth/auth-context";
import { useDeepLinkListener } from "./lib/auth/use-deep-link-listener";
import { useResyncPendingCaptures } from "./lib/captures/use-resync-pending";
import { useCascadeEntree } from "./lib/navigation/cascade-entree";
import { useRelancePeinture } from "./lib/navigation/relance-peinture";
import { ProjectProvider } from "./lib/projects/project-context";
import { AuthCallback } from "./screens/AuthCallback";
import { Home } from "./screens/Home";
import { SignIn } from "./screens/SignIn";

// Routes secondaires chargées à la demande (code splitting) : l'écran
// d'enregistrement (iOS-only), le styleguide, l'import et les écrans admin
// (personnes, dépenses IA) ne pèsent pas dans le bundle initial de l'accueil web.
const CaptureScreen = lazy(() =>
  import("./screens/CaptureScreen").then((m) => ({ default: m.CaptureScreen })),
);
const CaptureDetailScreen = lazy(() =>
  import("./screens/CaptureDetailScreen").then((m) => ({ default: m.CaptureDetailScreen })),
);
const ImportTranscriptScreen = lazy(() =>
  import("./screens/ImportTranscriptScreen").then((m) => ({ default: m.ImportTranscriptScreen })),
);
const FeedbackScreen = lazy(() =>
  import("./screens/FeedbackScreen").then((m) => ({ default: m.FeedbackScreen })),
);
const PersonsScreen = lazy(() =>
  import("./screens/PersonsScreen").then((m) => ({ default: m.PersonsScreen })),
);
const AiCostsScreen = lazy(() =>
  import("./screens/AiCostsScreen").then((m) => ({ default: m.AiCostsScreen })),
);
const TesterOnboardingScreen = lazy(() =>
  import("./screens/TesterOnboardingScreen").then((m) => ({ default: m.TesterOnboardingScreen })),
);
const FeedbacksAdminScreen = lazy(() =>
  import("./screens/FeedbacksAdminScreen").then((m) => ({ default: m.FeedbacksAdminScreen })),
);
const StyleguideScreen = lazy(() =>
  import("./screens/StyleguideScreen").then((m) => ({ default: m.StyleguideScreen })),
);
// Corbeille des prises abandonnées (incident 2026-09-04) : consultation,
// restauration et suppression définitive — route secondaire, comme /import.
const TrashScreen = lazy(() =>
  import("./screens/TrashScreen").then((m) => ({ default: m.TrashScreen })),
);
// Pages légales publiques (fiche de transparence + CGU) : consultables sans
// compte, liées depuis l'écran de connexion et le menu — routes lazy comme /ds.
const PrivacyScreen = lazy(() =>
  import("./screens/PrivacyScreen").then((m) => ({ default: m.PrivacyScreen })),
);
const TermsScreen = lazy(() =>
  import("./screens/TermsScreen").then((m) => ({ default: m.TermsScreen })),
);

interface AppProps {
  client: SupabaseClient;
}

// Ancien deep-link /captures(?captureId=…) : ouvrait autrefois l'accordéon
// sur l'accueil (l'accueil EST la liste des captures). Il mène désormais
// directement à la page dédiée /captures/:id — compat des bookmarks / liens
// d'import existants. Sans captureId (/captures nu), comportement inchangé :
// retour à l'accueil.
function CapturesRedirect() {
  const { search } = useLocation();
  const captureId = new URLSearchParams(search).get("captureId");
  if (captureId) return <Navigate to={`/captures/${captureId}`} replace />;
  return <Navigate to="/" replace />;
}

// Compat des anciens liens/bookmarks /?captureId=… (même deep-link que
// ci-dessus, mais déjà sur /) : redirige vers la page dédiée au lieu de
// rendre l'accueil avec l'accordéon ouvert.
function HomeRoute() {
  const { search } = useLocation();
  const captureId = new URLSearchParams(search).get("captureId");
  if (captureId) return <Navigate to={`/captures/${captureId}`} replace />;
  return <Home />;
}

function AppRoutes({ client }: { client: SupabaseClient }) {
  useDeepLinkListener();
  useResyncPendingCaptures();
  // Cascade d'entrée (DS §6.18) : elle accueille au premier affichage, elle ne
  // rejoue pas sur une navigation interne — sinon chaque « Retour » repart
  // d'un écran blanc le temps de l'animation.
  useCascadeEntree();
  // Écran blanc durable d'iOS 27 : contenu prêt mais non peint jusqu'à un
  // défilement. Rejoue un aller-retour d'un pixel (natif seulement).
  useRelancePeinture();
  return (
    // fallback null : le chunk d'une route lazy arrive en quelques ms (assets
    // locaux) — un spinner flasherait plus qu'il n'informerait.
    <Suspense fallback={null}>
      <Routes>
        <Route path="/sign-in" element={<SignIn />} />
        {/* /ds = styleguide vivant (axe E du DS). PUBLIC, comme /sign-in :
          la référence visuelle doit être consultable sans login (le
          magic-link ne fonctionne pas sur localhost). */}
        <Route path="/ds" element={<StyleguideScreen />} />
        {/* Pages légales publiques (fiche de transparence + CGU) : PAS de
            ProtectedRoute — consultables sans compte (lien depuis /sign-in,
            le menu de l'accueil, et le gate de consentement au premier login). */}
        <Route path="/confidentialite" element={<PrivacyScreen />} />
        <Route path="/conditions" element={<TermsScreen />} />
        <Route path="/auth/callback" element={<AuthCallback client={client} />} />
        <Route
          path="/capture"
          element={
            <ProtectedRoute>
              <CaptureScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/captures"
          element={
            <ProtectedRoute>
              <CapturesRedirect />
            </ProtectedRoute>
          }
        />
        {/* Page dédiée par capture : remplace le dépliage inline de la carte de
            l'accueil. Route lazy, comme les autres routes secondaires. */}
        <Route
          path="/captures/:id"
          element={
            <ProtectedRoute>
              <CaptureDetailScreen />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import"
          element={
            <ProtectedRoute>
              <ImportTranscriptScreen />
            </ProtectedRoute>
          }
        />
        {/* Corbeille des prises abandonnées (incident 2026-09-04). */}
        <Route
          path="/corbeille"
          element={
            <ProtectedRoute>
              <TrashScreen />
            </ProtectedRoute>
          }
        />
        {/* Rétroaction testeurs (item A) : canal dédié vers l'équipe. */}
        <Route
          path="/retroaction"
          element={
            <ProtectedRoute>
              <FeedbackScreen />
            </ProtectedRoute>
          }
        />
        {/* Ouvert à tous (item C confidentialité) : le garde-fou est la RLS
            `persons` (chacun voit son référentiel), plus l'accès à la page. */}
        <Route
          path="/persons"
          element={
            <ProtectedRoute>
              <PersonsScreen />
            </ProtectedRoute>
          }
        />
        {/* Phase B du chantier tracking dépenses IA : lecture de la vue
            `llm_usage_monthly` (phase A, PR #192), gate UI admin-only via
            AdminRoute — voir le commentaire sur fetchLlmUsageMonthly. */}
        <Route
          path="/depenses-ia"
          element={
            <AdminRoute>
              <AiCostsScreen />
            </AdminRoute>
          }
        />
        {/* Boîte de réception des rétroactions testeurs (item B) : la RLS
            feedbacks_select donne tout aux admins, le gate UI suit. */}
        <Route
          path="/retroactions"
          element={
            <AdminRoute>
              <FeedbacksAdminScreen />
            </AdminRoute>
          }
        />
        {/* Accueil des testeurs : fiches, courriels et suivi (admin). */}
        <Route
          path="/accueil-testeurs"
          element={
            <AdminRoute>
              <TesterOnboardingScreen />
            </AdminRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomeRoute />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Suspense>
  );
}

// Relais entre les deux contextes : ProjectProvider doit savoir DE QUI il charge
// les projets. Sans session, interroger `projects` renvoie « 200 [] », que l'app
// prenait pour « aucun projet » — d'où le sélecteur qui disparaissait.
function ProjetsDuCompte({ client, children }: { client: SupabaseClient; children: ReactNode }) {
  const { status, user } = useAuth();
  return (
    <ProjectProvider client={client} compte={status === "loading" ? undefined : (user?.id ?? null)}>
      {children}
    </ProjectProvider>
  );
}

export function App({ client }: AppProps) {
  return (
    <AuthProvider client={client}>
      <ProjetsDuCompte client={client}>
        <BrowserRouter>
          {/* Fil des gestes (DS §6.16) : sentinelle montée UNE fois, sous les
              providers — elle ne rend rien, elle consigne. */}
          <FilAriane />
          {/* Filet d'erreur (DS §6.16/§6.11) : une erreur d'écran ne blanchit
              que la zone des routes, jamais toute l'app. */}
          <AppErrorBoundary>
            <AppRoutes client={client} />
          </AppErrorBoundary>
          {/* Vitre dépolie sous la barre de statut iOS — hors Routes : présente
              sur tous les écrans, inerte sur le web (gate .is-native). */}
          <StatusBarScrim />
        </BrowserRouter>
      </ProjetsDuCompte>
    </AuthProvider>
  );
}
